import { loadCurrentProjectSourceTokens } from '../design-system/source-derived/load-current-project-source-tokens'
import {
  checkGenerationReadiness,
  type MachineGapCollection,
  type ReadinessReport,
} from './check-readiness'

/**
 * Task 5.5 (ontology-ux-contract cycle) — the ONE production binding of
 * `checkGenerationReadiness`, extracted so every consumer reads the SAME
 * judgment instead of re-wiring (and inevitably drifting from) its own copy.
 *
 * Before this file the identical wiring existed inline TWICE — in
 * `src/mcp/register-studio-tools.ts` (the WebMCP registration) and in
 * `start-app-generation-from-prompt.ts` (the Task 4 choke point) — each
 * carrying its own copy of the machine-validation honesty stub below. The
 * chat presentation layer (Task 5.5's `present-generation-readiness.ts`)
 * would have been the third copy; it is instead the third CONSUMER.
 *
 * "하나의 판단, 여러 소비" (spec §3): screen, chat, and WebMCP capability all
 * consume this one assembly.
 */

/**
 * Why `performed:false` and not an empty gap list: all three machine
 * admission vocabularies (library composition / external design knowledge /
 * UX application library admission) require a COMPLETED generation candidate
 * or an RDF graph to run against — no such candidate exists before
 * generation, so no pre-generation machine check runs today. Claiming
 * `performed:true, gaps: []` here would read as "checked and clean" (the
 * exact conflation Task 3 review round 1 flagged as Important). The reason
 * string is user-facing: `ReadinessPanel` renders it verbatim as the
 * machine-validation disclosure.
 */
export const PRE_GENERATION_MACHINE_VALIDATION_REASON =
  '기계 계약 검증은 생성 과정에서 수행됩니다. 생성 전에 미리 확인할 수 있는 검사가 아직 없습니다.'

/**
 * The honest pre-generation machine-gap collector. Shared verbatim by the
 * WebMCP registration (`register-studio-tools.ts`) and — through
 * `checkStudioGenerationReadiness` below — by the Task 4 choke point and the
 * Task 5.5 chat presentation.
 */
export async function collectPreGenerationMachineGaps(): Promise<MachineGapCollection> {
  return {
    performed: false,
    reason: PRE_GENERATION_MACHINE_VALIDATION_REASON,
  }
}

/**
 * The production readiness judgment for a Studio project. `projectId`
 * resolution stays with the caller (the choke point uses
 * `currentProjectId ?? 'unsaved'` because generation may CREATE the project;
 * the WebMCP registration requires an open server project) — everything
 * else is fixed here:
 *
 * - `loadTokens` is the real `loadCurrentProjectSourceTokens`, which throws
 *   by design when a REGISTERED source fails to load right now (fail-closed:
 *   `checkGenerationReadiness` folds that throw into `status:'error'` with
 *   one blocking item — never "ready to generate").
 * - `collectMachineGaps` is the honest `performed:false` stub above.
 *
 * Never rejects: `checkGenerationReadiness` catches everything into an
 * error-status report, so callers can always render SOMETHING truthful.
 */
export function checkStudioGenerationReadiness(
  projectId: string,
): Promise<ReadinessReport> {
  return checkGenerationReadiness({
    projectId,
    loadTokens: loadCurrentProjectSourceTokens,
    collectMachineGaps: collectPreGenerationMachineGaps,
  })
}
